'use client';

// ─── Role Definitions ───────────────────────────────────────────────────────
export type UserRole = 'farm_owner' | 'manager' | 'technician' | 'field_staff';

export interface UserProfile {
  id: string;
  name: string;
  initials: string;
  email: string;
  role: UserRole;
  farmId?: string;
}

// ─── Role Display Labels ─────────────────────────────────────────────────────
export const ROLE_LABELS: Record<UserRole, string> = {
  farm_owner: 'Farm Owner',
  manager: 'Manager',
  technician: 'Technician',
  field_staff: 'Field Staff',
};

export const ROLE_COLORS: Record<UserRole, { bg: string; text: string }> = {
  farm_owner: { bg: 'bg-purple-100', text: 'text-purple-700' },
  manager: { bg: 'bg-blue-100', text: 'text-blue-700' },
  technician: { bg: 'bg-teal-100', text: 'text-teal-700' },
  field_staff: { bg: 'bg-gray-100', text: 'text-gray-600' },
};

// ─── Permission Definitions ──────────────────────────────────────────────────
export type Permission =
  | 'view_dashboard' |'view_farms' |'manage_farms' |'view_flocks' |'manage_flocks' |'view_daily_records' |'create_daily_records' |'view_health' |'manage_health' |'view_mortality' |'create_mortality' |'view_inventory' |'manage_inventory' |'view_sales' |'manage_sales' |'create_invoices' |'view_expenses' |'create_expenses' |'approve_expenses' |'view_finance' |'manage_finance' |'view_payroll' |'manage_payroll' |'view_employees' |'manage_employees' |'view_reports' |'view_analytics' |'manage_settings' |'view_audit_logs' |'manage_procurement' |'view_procurement' |'view_tasks' |'manage_tasks' |'view_growth' |'manage_growth'
  // Legacy egg production permissions (kept for backward compat)
  | 'view_egg_production' | 'manage_egg_production' | 'approve_egg_production' | 'view_egg_inventory' | 'manage_egg_inventory'
  // Granular egg production permissions
  | 'egg_production.view' |'egg_production.create' |'egg_production.edit' |'egg_production.approve' |'egg_production.delete' |'egg_production.export' |'egg_inventory.view' |'egg_inventory.adjust' |'egg_grading.manage' |'egg_transfers.manage' |'egg_damages.manage' |'egg_reports.view' |'egg_settings.manage'
  // Granular procurement permissions
  | 'procurement.view' | 'procurement.requisition.create' | 'procurement.requisition.edit' | 'procurement.requisition.submit' | 'procurement.requisition.approve' | 'procurement.rfq.create' | 'procurement.quotation.manage' | 'procurement.comparison.evaluate' | 'procurement.po.create' | 'procurement.po.approve' | 'procurement.grn.create' | 'procurement.grn.approve' | 'procurement.invoice.view' | 'procurement.return.create' | 'procurement.reports.view' | 'procurement.export'
  // Supplier permissions
  | 'suppliers.view' | 'suppliers.create' | 'suppliers.edit' | 'suppliers.approve' | 'suppliers.archive' | 'suppliers.contacts.manage' | 'suppliers.documents.manage' | 'suppliers.bank_details.view' | 'suppliers.bank_details.manage' | 'suppliers.performance.view' | 'suppliers.evaluate' | 'suppliers.contracts.view' | 'suppliers.contracts.manage' | 'suppliers.suspend' | 'suppliers.blacklist' | 'suppliers.financials.view' | 'suppliers.reports.view' | 'suppliers.export';

// ─── Role → Permissions Map ──────────────────────────────────────────────────
const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  farm_owner: [
    'view_dashboard',
    'view_farms', 'manage_farms',
    'view_flocks', 'manage_flocks',
    'view_daily_records', 'create_daily_records',
    'view_health', 'manage_health',
    'view_mortality', 'create_mortality',
    'view_inventory', 'manage_inventory',
    'view_sales', 'manage_sales', 'create_invoices',
    'view_expenses', 'create_expenses', 'approve_expenses',
    'view_finance', 'manage_finance',
    'view_payroll', 'manage_payroll',
    'view_employees', 'manage_employees',
    'view_reports', 'view_analytics',
    'manage_settings', 'view_audit_logs',
    'view_procurement', 'manage_procurement',
    'view_tasks', 'manage_tasks',
    'view_growth', 'manage_growth',
    // Legacy
    'view_egg_production', 'manage_egg_production', 'approve_egg_production',
    'view_egg_inventory', 'manage_egg_inventory',
    // Granular
    'egg_production.view', 'egg_production.create', 'egg_production.edit',
    'egg_production.approve', 'egg_production.delete', 'egg_production.export',
    'egg_inventory.view', 'egg_inventory.adjust',
    'egg_grading.manage', 'egg_transfers.manage', 'egg_damages.manage',
    'egg_reports.view', 'egg_settings.manage',
    // Procurement granular
    'procurement.view', 'procurement.requisition.create', 'procurement.requisition.edit',
    'procurement.requisition.submit', 'procurement.requisition.approve',
    'procurement.rfq.create', 'procurement.quotation.manage', 'procurement.comparison.evaluate',
    'procurement.po.create', 'procurement.po.approve', 'procurement.grn.create',
    'procurement.grn.approve', 'procurement.invoice.view', 'procurement.return.create',
    'procurement.reports.view', 'procurement.export',
    // Supplier permissions
    'suppliers.view', 'suppliers.create', 'suppliers.edit', 'suppliers.approve', 'suppliers.archive',
    'suppliers.contacts.manage', 'suppliers.documents.manage',
    'suppliers.bank_details.view', 'suppliers.bank_details.manage',
    'suppliers.performance.view', 'suppliers.evaluate',
    'suppliers.contracts.view', 'suppliers.contracts.manage',
    'suppliers.suspend', 'suppliers.blacklist',
    'suppliers.financials.view', 'suppliers.reports.view', 'suppliers.export',
  ],
  manager: [
    'view_dashboard',
    'view_farms', 'manage_farms',
    'view_flocks', 'manage_flocks',
    'view_daily_records', 'create_daily_records',
    'view_health', 'manage_health',
    'view_mortality', 'create_mortality',
    'view_inventory', 'manage_inventory',
    'view_sales', 'manage_sales', 'create_invoices',
    'view_expenses', 'create_expenses', 'approve_expenses',
    'view_finance',
    'view_employees', 'manage_employees',
    'view_reports', 'view_analytics',
    'view_audit_logs',
    'view_procurement', 'manage_procurement',
    'view_tasks', 'manage_tasks',
    'view_growth', 'manage_growth',
    // Legacy
    'view_egg_production', 'manage_egg_production', 'approve_egg_production',
    'view_egg_inventory', 'manage_egg_inventory',
    // Granular
    'egg_production.view', 'egg_production.create', 'egg_production.edit',
    'egg_production.approve', 'egg_production.delete', 'egg_production.export',
    'egg_inventory.view', 'egg_inventory.adjust',
    'egg_grading.manage', 'egg_transfers.manage', 'egg_damages.manage',
    'egg_reports.view', 'egg_settings.manage',
    // Procurement granular
    'procurement.view', 'procurement.requisition.create', 'procurement.requisition.edit',
    'procurement.requisition.submit', 'procurement.requisition.approve',
    'procurement.rfq.create', 'procurement.quotation.manage', 'procurement.comparison.evaluate',
    'procurement.po.create', 'procurement.po.approve', 'procurement.grn.create',
    'procurement.grn.approve', 'procurement.invoice.view', 'procurement.return.create',
    'procurement.reports.view', 'procurement.export',
    // Supplier permissions
    'suppliers.view', 'suppliers.create', 'suppliers.edit', 'suppliers.approve',
    'suppliers.contacts.manage', 'suppliers.documents.manage',
    'suppliers.bank_details.view', 'suppliers.bank_details.manage',
    'suppliers.performance.view', 'suppliers.evaluate',
    'suppliers.contracts.view', 'suppliers.contracts.manage',
    'suppliers.suspend',
    'suppliers.financials.view', 'suppliers.reports.view', 'suppliers.export',
  ],
  technician: [
    'view_dashboard',
    'view_farms',
    'view_flocks', 'manage_flocks',
    'view_daily_records', 'create_daily_records',
    'view_health', 'manage_health',
    'view_mortality', 'create_mortality',
    'view_inventory',
    'view_sales',
    'view_expenses', 'create_expenses',
    'view_reports',
    'view_procurement',
    'view_tasks',
    'view_growth', 'manage_growth',
    // Legacy
    'view_egg_production', 'manage_egg_production',
    'view_egg_inventory',
    // Granular — technician can view, create, edit but NOT approve/delete/adjust/settings
    'egg_production.view', 'egg_production.create', 'egg_production.edit',
    'egg_production.export',
    'egg_inventory.view',
    'egg_grading.manage', 'egg_transfers.manage',
    'egg_reports.view',
    // Procurement
    'procurement.view', 'procurement.requisition.create', 'procurement.requisition.submit',
    'procurement.grn.create',
    // Supplier view
    'suppliers.view', 'suppliers.performance.view', 'suppliers.contracts.view',
    'suppliers.reports.view',
  ],
  field_staff: [
    'view_dashboard',
    'view_farms',
    'view_flocks',
    'view_daily_records', 'create_daily_records',
    'view_health',
    'view_mortality', 'create_mortality',
    'view_inventory',
    'view_tasks',
    'view_growth',
    // Legacy
    'view_egg_production',
    'view_egg_inventory',
    // Granular — field staff can only view and create (record production)
    'egg_production.view', 'egg_production.create',
    'egg_inventory.view',
    // Supplier view only
    'suppliers.view',
  ],
};

// ─── Nav Item Visibility ─────────────────────────────────────────────────────
export const NAV_PERMISSIONS: Record<string, Permission> = {
  'nav-dashboard': 'view_dashboard',
  'nav-farms': 'view_farms',
  'nav-flocks': 'view_flocks',
  'nav-birds': 'view_flocks',
  'nav-birds-movements': 'view_flocks',
  'nav-birds-daily': 'view_daily_records',
  'nav-birds-transfers': 'view_flocks',
  'nav-birds-reconciliation': 'view_flocks',
  'nav-birds-culling': 'view_flocks',
  'nav-birds-sales': 'view_sales',
  'nav-daily-records': 'view_daily_records',
  'nav-feed': 'view_inventory',
  'nav-health': 'view_health',
  'nav-mortality': 'view_mortality',
  'nav-growth': 'view_growth',
  'nav-eggs': 'view_egg_production',
  'nav-inventory': 'view_inventory',
  'nav-procurement': 'view_procurement',
  'nav-invoice-matching': 'procurement.invoice.view',
  'nav-suppliers': 'suppliers.view',
  'nav-supplier-onboarding': 'suppliers.view',
  'nav-supplier-approvals': 'suppliers.approve',
  'nav-sales': 'view_sales',
  'nav-customers': 'view_sales',
  'nav-expenses': 'view_expenses',
  'nav-finance': 'view_finance',
  'nav-employees': 'view_employees',
  'nav-payroll': 'view_payroll',
  'nav-staff-invitations': 'manage_employees',
  'nav-tasks': 'view_tasks',
  'nav-biosecurity': 'view_health',
  'nav-assets': 'view_inventory',
  'nav-reports': 'view_reports',
  'nav-analytics': 'view_analytics',
  'nav-notifications': 'view_dashboard',
  'nav-documents': 'view_reports',
  'nav-audit': 'view_audit_logs',
  'nav-settings': 'manage_settings',
  'nav-profile': 'view_dashboard',
};

// ─── Helper Functions ────────────────────────────────────────────────────────
export function hasPermission(role: UserRole, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role]?.includes(permission) ?? false;
}

export function canAccess(role: UserRole, navId: string): boolean {
  const requiredPermission = NAV_PERMISSIONS[navId];
  if (!requiredPermission) return true; // no restriction = visible to all
  return hasPermission(role, requiredPermission);
}

// ─── Mock Current User (simulates auth session) ──────────────────────────────
// In a real app this would come from Supabase auth / session context.
// Change `role` here to test different access levels.
export const CURRENT_USER: UserProfile = {
  id: 'user-001',
  name: 'Okello James',
  initials: 'OJ',
  email: 'okello@idudi.farm',
  role: 'manager', // Change to: 'farm_owner' | 'manager' | 'technician' | 'field_staff'
  farmId: 'farm-idudi',
};

export { ROLE_PERMISSIONS };